import React, { useEffect, useState } from 'react';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { useParams } from 'react-router-dom';
import { Box, Button, Paper, Typography, styled } from '@mui/material';
import Grid from '@mui/material/Grid2';
import api from '../../services/api';

// Styled components inspired by NestedTaskList.jsx
const StyledPaper = styled(Paper)(({ theme }) => ({
  overflow: 'hidden', // Prevents content from breaking layout
}));

// Header row for the main list of sprints
const SprintsListHeaderRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: 'bold',
  alignItems: 'center',
}));

// Represents a single sprint row
const SprintEntryRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  alignItems: 'center',
}));

const TrackDetails = () => {
  const { trackId } = useParams();
  const { currentWorkspace } = useWorkspace();
  const [track, setTrack] = useState(null);
  const [sprints, setSprints] = useState([]);

  const fetchTrack = async () => {
    try {
      if (!trackId || !currentWorkspace?.id) return;
      const { data } = await api.get(`tracks/${trackId}/`, {
        params: { workspace_id: currentWorkspace.id },
      });
      setTrack(data);
    } catch (error) {
      console.error('Error fetching track details:', error);
      setTrack(null);
    }
  };

  const fetchSprints = async () => {
    try {
      if (!trackId || !currentWorkspace?.id) return;
      const { data } = await api.get(`track-sprints/${trackId}`, {
        params: { workspace_id: currentWorkspace.id },
      });
      setSprints(data.payload || []); // Ensure payload is an array
    } catch (error) {
      console.error('Error fetching sprints:', error);
      setSprints([]);
    }
  };

  const handleAddSprint = async () => {
    try {
      if (!trackId || !currentWorkspace?.id) return;
      await api.post('track-sprints/', {
        track_id: trackId,
        workspace_id: currentWorkspace.id,
        // Consider adding default name/dates if API requires
      });
      fetchSprints();
    } catch (error) {
      console.error('Error adding sprint:', error);
    }
  };

  useEffect(() => {
    if (trackId && currentWorkspace?.id) {
      fetchTrack();
      fetchSprints();
    }
  }, [trackId, currentWorkspace]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {track?.name}
      </Typography>
      <StyledPaper>
        <Typography variant="h6" gutterBottom sx={{ p: 2, pb: 1 }}>Sprints</Typography>
        
        <SprintsListHeaderRow container spacing={1}>
          <Grid size={3}><Typography variant="subtitle2">Name</Typography></Grid>
          <Grid size={2}><Typography variant="subtitle2">Start Date</Typography></Grid>
          <Grid size={2}><Typography variant="subtitle2">End Date</Typography></Grid>
          <Grid size={5}><Typography variant="subtitle2">Goal</Typography></Grid>
        </SprintsListHeaderRow>

        {sprints.length > 0 ? (
          sprints.map((sprint) => (
            <SprintEntryRow container spacing={1} key={sprint.id}>
              <Grid size={3}>
                <Typography variant="subtitle1">{sprint.name}</Typography>
              </Grid>
              <Grid size={2}>
                <Typography variant="body2">{sprint.start_date}</Typography>
              </Grid>
              <Grid size={2}>
                <Typography variant="body2">{sprint.end_date}</Typography>
              </Grid>
              <Grid size={5}>
                {sprint.sprint_goals?.length > 0 ? (
                  sprint.sprint_goals.map((goal) => (
                    <Typography
                      key={goal.id}
                      variant="body2"
                      sx={{ textDecoration: goal.is_completed ? 'line-through' : 'none', whiteSpace: 'pre-wrap' }}
                    >
                      {goal.goal}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No goal
                  </Typography>
                )}
              </Grid>
            </SprintEntryRow>
          ))
        ) : (
          <Box sx={{p:2, borderBottom: `1px solid ${theme => theme.palette.divider}`}}>
             <Typography sx={{ textAlign: 'center'}}>No sprints available for this track.</Typography>
          </Box>
        )}
        <Box sx={{p: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Button variant="contained" onClick={handleAddSprint}>
            Add Sprint
          </Button>
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default TrackDetails;
